import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import type { SortingState } from '@tanstack/vue-table';
import { useQueryClient } from '@tanstack/vue-query';
import { useToast } from 'vue-toastification';
import { parseISO } from 'date-fns';
import type { FacetSelection } from '@/components/ui/data-table';
import { store } from '@/plugins/store';
import { i18n } from '@/plugins/internationalization/i18n';
import type { RecordsListFetchParams } from '@/types';
import { useRecordsAggregation, weekInterval, type RecordFacetDefinitions, type RecordRow } from './useRecordsAggregation';

/*
 * Both strings live under the shared `records` namespace and read the same on every records page,
 * so they are fixed here rather than configured per page - a knob every caller sets to the same
 * value is not a knob.
 */
const SUBTITLE_KEY = 'views.pigs.records.table.subtitle';
const SET_CHANGED_MESSAGE_KEY = 'views.pigs.records.messages.setChangedAfterEdit';

/**
 * The part of a query hook's result the page reads, declared structurally rather than as
 * vue-query's own return type so a hook can be handed in whether its refs come back writable or
 * readonly.
 */
export type RecordsQueryResult<TRow> = {
    data: Readonly<Ref<TRow[] | undefined>>;
    isFetching: Readonly<Ref<boolean>>;
    isError: Readonly<Ref<boolean>>;
    refetch: () => unknown;
};

export type RecordsPageConfig<TRow extends RecordRow> = {
    /**
     * The page's query module, from `plugins/store/actions/queries/<domain>/`.
     *
     * The hook and its root key travel together because they have to agree: the root key is what the
     * after-edit reload invalidates, and a key belonging to some other hook would invalidate a cache
     * entry nothing reads - leaving the reload silently doing nothing, with no error to notice.
     */
    records: {
        use: (params: ComputedRef<RecordsListFetchParams>, options: { enabled: ComputedRef<boolean> }) => RecordsQueryResult<TRow>;
        rootKey: unknown[];
    };
    /** The filterable columns, as a getter so its translated labels are read per render. */
    facets: () => RecordFacetDefinitions<TRow>;
};

/**
 * Everything a records page holds that is not server data: the fetch, the selected week bin, the
 * header facet selection, and the table's sort.
 *
 * No paging: the table's height is already capped and internally scrollable (`RecordsTable`'s
 * `maxBodyHeight`), so a page size only ever changed how many clicks it took to reach a row, never
 * how much screen the table took up. Every row the query loads renders; the reader scrolls.
 *
 * A page supplies its query hook and its filterable columns; nothing here knows which registrations
 * it is looking at. The date range and the location scope come from the global pickers, so a page
 * built on this needs no picker of its own.
 */
export function useRecordsPage<TRow extends RecordRow>(config: RecordsPageConfig<TRow>) {
    const queryClient = useQueryClient();
    const toast = useToast();

    const fetchParams = computed<RecordsListFetchParams>(() => ({
        period: store.getters.getSelectedPeriodByDate,
        // Pig type narrows the scope here rather than on the server, which has no single notion of
        // which barns are weaners.
        locationIds: store.getters.getSelectedSiteIdsFilteredByPigType,
    }));

    const isQueryEnabled = computed(() => fetchParams.value.locationIds.length > 0);
    const { data, isFetching, isError, refetch } = config.records.use(fetchParams, { enabled: isQueryEnabled });

    const rows = computed(() => data.value ?? []);

    const selectedWeekStart = ref<string | null>(null);
    const columnFilters = ref<FacetSelection>({});
    const sorting = ref<SortingState>([{ id: 'date', desc: true }]);

    /*
     * Both filters are applied here rather than inside the table. The facet vocabulary is already
     * built here, from the unfiltered rows, and keeping the matching beside it means the totals and
     * the after-edit comparison below read the same array the table renders.
     */
    const { bins, facetOptions, visibleRows, totals } = useRecordsAggregation(rows, selectedWeekStart, columnFilters, config.facets);

    /**
     * The days of the selected week that the table can actually show; null while nothing is selected.
     *
     * Narrower than the week's own Monday–Sunday span wherever the week straddles a period boundary:
     * the rows are scoped to the period, so those outside days hold nothing the table renders. The
     * bounds are read back off the serialised `period` the query itself sends, so this can never
     * disagree with the rows on screen, and `yyyy-MM-dd` days compare directly with `<`/`>` - parsing
     * is needed only for display.
     */
    const selectedInterval = computed<[string, string] | null>(() => {
        if (selectedWeekStart.value === null) {
            return null;
        }

        const [weekFrom, weekTo] = weekInterval(selectedWeekStart.value);

        // A bound the period does not carry leaves that side of the week at its own boundary.
        const periodDays = new URLSearchParams(fetchParams.value.period);
        const periodFrom = periodDays.get('from') ?? weekFrom;
        const periodTo = periodDays.get('to') ?? weekTo;

        const from = weekFrom < periodFrom ? periodFrom : weekFrom;
        const to = weekTo > periodTo ? periodTo : weekTo;

        return [from, to];
    });

    const hasActiveFilter = computed(() => selectedWeekStart.value !== null || Object.values(columnFilters.value).some((selected) => selected.length > 0));

    /** Names the selected week under the table heading; blank while nothing is selected. */
    const subtitle = computed(() => {
        if (selectedInterval.value === null) {
            return '';
        }

        const [from, to] = selectedInterval.value;

        return i18n.global
            .t(SUBTITLE_KEY)
            .replace('%FROM%', i18n.global.d(parseISO(from), 'short'))
            .replace('%TO%', i18n.global.d(parseISO(to), 'short'));
    });

    function selectWeek(weekStart: string) {
        selectedWeekStart.value = selectedWeekStart.value === weekStart ? null : weekStart;
    }

    function resetSelection() {
        selectedWeekStart.value = null;
    }

    // A week outside the new range would filter the table down to nothing with no visible cause.
    watch(
        () => fetchParams.value.period,
        () => {
            resetSelection();
        }
    );

    /**
     * Reload once the formula modal has closed, and say so when the visible set changed under the
     * reader.
     *
     * The edited registration may have moved out of the active filter, or been deleted outright;
     * the message covers both, because neither is distinguishable from the other here. It names
     * rows rather than the edited one on purpose - a save soft-deletes the old registration and
     * chains a new one, so which row left cannot be known.
     */
    async function refreshAfterFormActivity() {
        const rowsBefore = visibleRows.value.length;

        await queryClient.invalidateQueries({ queryKey: config.records.rootKey });

        if (hasActiveFilter.value && visibleRows.value.length < rowsBefore) {
            toast.info(i18n.global.t(SET_CHANGED_MESSAGE_KEY));
        }
    }

    // What a page renders, plus the unfiltered loaded rows themselves for a page that needs to
    // inspect the whole loaded set rather than what the filters currently leave of it (Sold's
    // integrator column and Deaths' Section/Category columns each decide their own visibility this
    // way). The resolved week interval and the filter predicate stay internal.
    return {
        bins,
        columnFilters,
        facetOptions,
        isError,
        isFetching,
        refetch,
        refreshAfterFormActivity,
        resetSelection,
        rows,
        selectWeek,
        selectedWeekStart,
        sorting,
        subtitle,
        totals,
        visibleRows,
    };
}
